'use client';
// La langue de la page, pour les composants clients. Fournie par le layout racine, qui la
// tient de `getLang()`. Hors fournisseur (test, composant isolé), le français.

import { createContext, useContext, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { DEFAULT_LOCALE, sansLangue, type Locale } from '@/lib/i18n/config';
import { creerT, type Surcharges, type T } from '@/lib/i18n/t';

const LangContext = createContext<Locale>(DEFAULT_LOCALE);
// Les tournures reprises en administration : lues par le layout, passées ici une fois.
const SurchargesContext = createContext<Surcharges | undefined>(undefined);

export function LangProvider({ lang, surcharges, children }: { lang: Locale; surcharges?: Surcharges; children: React.ReactNode }) {
  return (
    <LangContext.Provider value={lang}>
      <SurchargesContext.Provider value={surcharges}>{children}</SurchargesContext.Provider>
    </LangContext.Provider>
  );
}

export function useLang(): Locale {
  return useContext(LangContext);
}

/** Les textes fixes dans la langue de la page : `const t = useT(); t('bs.onglets.verdict')`. */
export function useT(): T {
  const lang = useLang();
  const surcharges = useContext(SurchargesContext);
  return useMemo(() => creerT(lang, surcharges), [lang, surcharges]);
}

/**
 * Le chemin de la page SANS son préfixe de langue. À employer partout où un composant compare
 * l'adresse courante à une route (« quelle entrée de la navigation est active ? ») : comparer
 * `/en/acteurs` à `/acteurs` ne trouverait jamais rien.
 */
export function useCheminSansLangue(): string {
  return sansLangue(usePathname() ?? '/');
}
